import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import MapPickerModal from './MapPickerModal'
import TripRouteModal from './TripRouteModal'
import ReceiptAddModal from './ReceiptAddModal'
import arrowLeft from '../assets/images/icons/arrow_left.png'
import arrowRight from '../assets/images/icons/arrow_right.png'
import { 
  calculateFuelUsedLiters,
  calculateAvgSpeedKmh,
  calculateTripDurationMinutes,
  formatDurationHHMM
} from '../helpers/calculateConsumption'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const fields = [
  ['date', 'TRIPLOG.TRIPS.TABLE.date'],
  ['vehicleId', 'TRIPLOG.TRIPS.TABLE.vehicleId'],
  ['receiptId', 'TRIPLOG.TRIPS.TABLE.receiptId'],
  ['spz', 'vehicle_license_plate'],
  ['VIN', 'TRIPLOG.TRIPS.TABLE.VIN'],
  ['driver', 'TRIPLOG.TRIPS.TABLE.driver'],
  ['startTimeIso', 'TRIPLOG.TRIPS.TABLE.startTimeIso'],
  ['startPlace', 'TRIPLOG.TRIPS.TABLE.startPlace'],
  ['endTimeIso', 'TRIPLOG.TRIPS.TABLE.endTimeIso'],
  ['endPlace', 'TRIPLOG.TRIPS.TABLE.endPlace'],
  ['odoStart', 'TRIPLOG.TRIPS.TABLE.odoStart'],
  ['odoEnd', 'TRIPLOG.TRIPS.TABLE.odoEnd'],
  ['distanceKm', 'TRIPLOG.TRIPS.TABLE.distanceKm'],
  ['duration', 'TRIPLOG.TRIPS.TABLE.durationMinutes'],
  ['purpose', 'TRIPLOG.TRIPS.TABLE.purpose'],
  ['avgSpeedKmh', 'TRIPLOG.TRIPS.TABLE.avgSpeedKmh'],
  ['fuelUsed', 'TRIPLOG.TRIPS.TABLE.fuelUsedLiters'],
  ['model', 'TRIPLOG.TRIPS.TABLE.model'],
  ['typ_variant_verzia', 'TRIPLOG.TRIPS.TABLE.typ_variant_verzia'],
  ['note', 'TRIPLOG.TRIPS.TABLE.note'],
  ['mode', 'TRIPLOG.TRIPS.TABLE.mode'],
  ['gpsStart', 'TRIPLOG.TRIPS.TABLE.gpsStart'],
  ['gpsEnd', 'TRIPLOG.TRIPS.TABLE.gpsEnd']
]

const PURPOSE_KEYS = [
  'CLIENT_MEETING',
  'DELIVERY',
  'TRAINING',
  'WAREHOUSE_TRANSFER',
  'SERVICE',
  'ADMIN',
  'PRIVATE',
  'OTHER'
]

const Row = ({ htmlFor, label, children, layout }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
    <label
      htmlFor={htmlFor}
      style={{ width: layout.labelW, fontWeight: 700, fontSize: layout.labelFont }}
    >
      {label}
    </label>

    <div style={{ display: 'flex', alignItems: 'center', width: layout.inputW }}>
      {children}
    </div>
  </div>
)

const TripDetailModal = ({
  open,
  onClose,
  trip,
  onSaved,
  onPrevTrip,
  onNextTrip,
  hasPrevTrip = false,
  hasNextTrip = false
}) => {
const modalRef = useRef(null)
const formatForDateInput = (value) => {
  if (!value) return ''

  if (typeof value === 'string') {
    const normalized = value.trim()

    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      return normalized
    }

    const skMatch = normalized.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
    if (skMatch) {
      const [, day, month, year] = skMatch
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
  }

  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toISOString().split('T')[0]
}
  const { t } = useTranslation()
const getPurposeLabel = (key) => t(`TRIPLOG.PURPOSES.${key}`)

  const [vw, setVw] = useState(window.innerWidth)
let layout = {}

if (vw >= 1920) {
  layout = {
    labelW: 350,
    inputW: 600,
    inputH: 20,
    labelFont: 15,
    inputFont: 14,
    rowGap: 4
  }
}
else if (vw >= 1366) {
  layout = {
    labelW: 300,
    inputW: 520,
    inputH: 18,
    labelFont: 14,
    inputFont: 13,
    rowGap: 3
  }
}
else {
  layout = {
    labelW: 240,
    inputW: 420,
    inputH: 18,
    labelFont: 13,
    inputFont: 12,
    rowGap: 3
  }
}

const inputStyle = {
  height: layout.inputH,
  fontSize: layout.inputFont,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #999',
  width: '100%'
}

  const [form, setForm] = useState({})
  const [original, setOriginal] = useState({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
const [fieldErrors, setFieldErrors] = useState({})
  const [editMode, setEditMode] = useState(false)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [vehiclesById, setVehiclesById] = useState({})
const [mapOpen, setMapOpen] = useState(false)
const [routeOpen, setRouteOpen] = useState(false)
const [mapField, setMapField] = useState(null)
const [receipts, setReceipts] = useState([])
const [userTrips, setUserTrips] = useState([])
const [receiptModalOpen, setReceiptModalOpen] = useState(false)
const [showFullscreenHint, setShowFullscreenHint] = useState(false)
const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })
const [mapHintTimer, setMapHintTimer] = useState(null)
const showTripNavigation = Boolean(onPrevTrip || onNextTrip)

const filteredReceipts = useMemo(() => {
  if (!form?.vehicleId) return []

  const assignedReceiptIds = new Set(
    userTrips
      .filter((tripItem) => String(tripItem?.id) !== String(form?.id))
      .map((tripItem) => String(tripItem?.receiptId ?? ''))
      .filter((receiptId) => receiptId)
  )

  return receipts.filter((receipt) => {
    if (String(receipt?.vehicleId) !== String(form.vehicleId)) return false

    const assignedTripId = receipt?.tripId ?? receipt?.trip_id ?? null
    if (assignedTripId && String(assignedTripId) !== String(form?.id)) return false

    if (assignedReceiptIds.has(String(receipt?.id))) return false

    return true
  })
}, [form?.vehicleId, form?.id, receipts, userTrips])

  useEffect(() => {
    const handleResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const modalMaxWidth =
    vw >= 2400 ? '28vw' :
    vw >= 1920 ? '40vw' :
    vw >= 1366 ? '45vw' :
    '36vw'

  const modalMaxHeight =
    vw >= 1920 ? '98vh' : '95vh'

  const modalTitleFontSize =
    vw >= 1920 ? 24 :
    vw >= 1366 ? 24 :
    18

const loadVehicles = useCallback(async () => {
  if (!trip?.userId) return

  try {
    const res = await fetchWithAuth(`/users/${trip.userId}/vehicles`)
    if (!res.ok) return

    const data = await res.json()
    if (!Array.isArray(data)) return

    const index = data.reduce((acc, vehicle) => {
      if (vehicle?.id == null) return acc
      acc[String(vehicle.id)] = vehicle
      return acc
    }, {})

    setVehiclesById(index)
  } catch {}
}, [trip?.userId])

const loadReceipts = useCallback(async () => {
  if (!trip?.userId) return

  try {
    const res = await fetchWithAuth(`/users/${trip.userId}/receipts`)
    if (!res.ok) return

    const data = await res.json()
    if (!Array.isArray(data)) return

    setReceipts(data)
  } catch {}
}, [trip?.userId])

const loadTrips = useCallback(async () => {
  try {
    const res = await fetchWithAuth('/triplog/trips')
    if (!res.ok) return

    const data = await res.json()
    if (!Array.isArray(data)) return

    setUserTrips(data)
  } catch {}
}, [])

useEffect(() => {
  if (!open || !trip) return

  loadVehicles()
  loadReceipts()
  loadTrips()

  setForm(trip)
  setOriginal(trip)
  setEditMode(false)
  setMessage(null)
}, [open, trip, loadVehicles, loadReceipts, loadTrips])

useEffect(() => {
  if (!form?.receiptId) return
  const selectedReceipt = receipts.find((receipt) => String(receipt.id) === String(form.receiptId))
  if (selectedReceipt && String(selectedReceipt.vehicleId) !== String(form.vehicleId)) {
    setForm((prev) => ({ ...prev, receiptId: '' }))
  }
}, [form?.vehicleId, form?.receiptId, receipts])

useEffect(() => {
  if (!open) return

  const trapModalFocus = (e) => {
    if (e.key !== 'Tab') return false

    const modalEl = modalRef.current
    if (!modalEl) return false

    const focusable = Array.from(
      modalEl.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.disabled && el.getAttribute('aria-hidden') !== 'true')

    if (!focusable.length) {
      e.preventDefault()
      modalEl.focus()
      return true
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement
    const isInsideModal = modalEl.contains(active)

    if (e.shiftKey) {
      if (!isInsideModal || active === first) {
        e.preventDefault()
        last.focus()
        return true
      }
    } else if (!isInsideModal || active === last) {
      e.preventDefault()
      first.focus()
      return true
    }

    return false
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (routeOpen) {
        setRouteOpen(false)
        return
      }
      if (mapOpen) {
        setMapOpen(false)
        setMapField(null)
        return
      }
      if (editMode) {
        setEditMode(false)
      } else {
        onClose()
      }
      return
    }

    if (routeOpen || mapOpen) return

    if (trapModalFocus(e)) return

    if (editMode) return

    if (e.key === 'ArrowLeft' && hasPrevTrip) {
      e.preventDefault()
      onPrevTrip?.()
      return
    }

    if (e.key === 'ArrowRight' && hasNextTrip) {
      e.preventDefault()
      onNextTrip?.()
    }
  }

  window.addEventListener('keydown', handleKeyDown)

  return () => window.removeEventListener('keydown', handleKeyDown)
}, [open, editMode, onClose, routeOpen, mapOpen, hasPrevTrip, hasNextTrip, onPrevTrip, onNextTrip])

useEffect(() => {
  if (!open) return

  const modalEl = modalRef.current
  if (!modalEl) return

  const focusable = modalEl.querySelector(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  if (focusable && typeof focusable.focus === 'function') {
    focusable.focus()
    return
  }

  modalEl.focus()
}, [open, editMode])

  const getTripVehicleLabel = useCallback((tripObj) => {
    const vehicleFromList = vehiclesById[String(tripObj?.vehicleId)]
    const vehicle = vehicleFromList || tripObj?.vehicle || tripObj?.tripVehicle
    return (
      vehicle?.name ||
      vehicle?.model ||
      vehicle?.licensePlate ||
      vehicle?.spz ||
      tripObj?.vehicleId ||
      '-'
    )
  }, [vehiclesById])

  const tripId = useMemo(() => trip?.id, [trip])

const handleMapSelect = (address) => {
  setForm(prev => ({
    ...prev,
    [mapField]: address
  }))
}

  const saveTrip = async () => {
    if (!tripId) return

const odoStart = Number(form.odoStart)
const odoEnd = Number(form.odoEnd)

if (
  Number.isFinite(odoStart) &&
  Number.isFinite(odoEnd) &&
  odoStart > odoEnd
) {
  setFieldErrors({
    odoStart: true,
    odoEnd: true
  })

  setMessage({ message: t('TRIPLOG.ERRORS.ODO_INVALID'), type: 'error' })
  return
}

const startTime = form.startTimeIso ? new Date(form.startTimeIso) : null
const endTime = form.endTimeIso ? new Date(form.endTimeIso) : null

if (
  startTime &&
  endTime &&
  !isNaN(startTime.getTime()) &&
  !isNaN(endTime.getTime()) &&
  endTime < startTime
) {
  setFieldErrors({
    startTimeIso: true,
    endTimeIso: true
  })

  setMessage({ message: t('TRIPLOG.ERRORS.TIME_INVALID'), type: 'error' })
  return
}

setFieldErrors({})

    try {
      setLoading(true)

      const computedAvgSpeedKmh = calculateAvgSpeedKmh(form)
      const payload = {
        ...form,
        avgSpeedKmh: computedAvgSpeedKmh != null ? computedAvgSpeedKmh : null
      }

      const res = await fetchWithAuth(`/triplog/trips/${tripId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

if (!res.ok) {
  const payload = await res.json().catch(() => null)
  const backendError = payload?.error || payload?.message || ''

  if (res.status === 409) {
    setMessage({
      message: t('TRIPLOG.ERRORS.RECEIPT_ALREADY_ASSIGNED', {
        defaultValue: 'Táto účtenka je už priradená k inej jazde.'
      }),
      type: 'error'
    })
    return
  }

  if (typeof backendError === 'string' && backendError.trim()) {
    setMessage({ message: backendError, type: 'error' })
    return
  }

  setMessage({
    message: t('TRIPLOG.ERRORS.SAVE_FAILED', {
      defaultValue: 'Uloženie jazdy zlyhalo.'
    }),
    type: 'error'
  })
  return
}

const updated = await res.json().catch(() => payload)

const vehicle = vehiclesById[String(updated.vehicleId)]

const enrichedUpdated = {
  ...updated,
  spz: vehicle?.licensePlate || vehicle?.spz || updated.spz || '',
  VIN: vehicle?.vin || updated.VIN || '',
  model: vehicle?.model || updated.model || '',
  typ_variant_verzia:
    vehicle?.variantType || updated.typ_variant_verzia || ''
}

setOriginal(enrichedUpdated)
setForm(enrichedUpdated)
setEditMode(false)
//setMessage({ message: t('SAVED'), type: 'success' })
onSaved?.(enrichedUpdated)
    } catch {
      setMessage({
        message: t('TRIPLOG.ERRORS.SAVE_FAILED', {
          defaultValue: 'Uloženie jazdy zlyhalo.'
        }),
        type: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  if (!open || !trip) return null

  const isGpsMode =
    form.mode === 'gps' || form.mode === 'GPS'

const isManualMode =
  form.mode === 'manual' || form.mode === 'MANUAL'

const manualReadonlyFields = [
  'spz',
  'VIN',
  'model',
  'typ_variant_verzia',
  'fuelUsed',
  'avgSpeedKmh',
  'gpsStart',
  'gpsEnd',
  'mode'
]

const renderField = ([key, labelKey]) => {

if (key === 'receiptId') {
  return (
    <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
      <div style={{ display: 'flex', gap: 8, width: '100%' }}>
        <select
          value={form.receiptId || ''}
          disabled={!editMode}
          onChange={e =>
            setForm(prev => ({
              ...prev,
              receiptId: e.target.value || ''
            }))
          }
          style={{
            ...inputStyle,
            height: 34,
            background: !editMode ? '#ddd' : '#fff',
            color: '#000',
          }}
        >
          <option value="">--</option>

          {filteredReceipts.map(receipt => {
            const vehicle = vehiclesById[String(receipt.vehicleId)]

            return (
              <option key={receipt.id} value={receipt.id}>
                {receipt.date ? new Date(receipt.date).toLocaleDateString('sk-SK') : '-'} ·
                {vehicle?.name || vehicle?.model || '-'} ·
                {vehicle?.licensePlate || vehicle?.spz || '-'} ·
                {receipt.fuel_name || '-'} ·
                {receipt.units || '-'} {receipt.unit || '-'} ·
                {receipt.total_price || '-'} ·
                {receipt.store_name || '-'}
              </option>
            )
          })}
        </select>

      </div>
    </Row>
  )
}

if (key === 'startPlace' || key === 'endPlace') {
  return (
    <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
      <div style={{ display: 'flex', gap: 6, width: '100%' }}>
        <input
          type="text"
          value={form[key] || ''}
          disabled={!editMode || isGpsMode}
          onChange={e =>
            setForm(prev => ({ ...prev, [key]: e.target.value }))
          }
          style={{
            ...inputStyle,
            background:
  !editMode || isGpsMode
    ? '#ddd'
    : '#fff'
          }}
        />

{editMode && !isGpsMode && (
  <button
    type="button"
    onClick={() => {
      setMapField(key)
      setMapOpen(true)
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = '#9ad1bb'

      const timer = setTimeout(() => {
        setShowFullscreenHint(true)
      }, 600)

      setMapHintTimer(timer)
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = '#f5f5f5'
      e.currentTarget.style.transform = 'scale(1)'
      e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'

      if (mapHintTimer) {
        clearTimeout(mapHintTimer)
      }

      setShowFullscreenHint(false)
    }}
    onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
    onMouseDown={(e) => {
      e.currentTarget.style.transform = 'scale(0.96)'
      e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
    }}
    onMouseUp={(e) => {
      e.currentTarget.style.transform = 'scale(1)'
      e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
    }}
    style={{
      padding: '0 8px',
      borderRadius: 6,
      border: '1px solid #107c6b',
      background: '#f5f5f5',
      cursor: 'pointer',
      fontWeight: 700,
      boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
      transition: 'transform 0.05s ease, box-shadow 0.1s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <img
      src={require('../assets/images/icons/map.png')}
      alt=""
      style={{ width: 34, height: 30, pointerEvents: 'none' }}
    />
  </button>
)}
      </div>
    </Row>
  )
}
if (isManualMode && (key === 'gpsStart' || key === 'gpsEnd')) {
  return null
}
  let displayValue = form[key] ?? ''

  if (key === 'mode') {
    const modeKey = (form.mode || '').toUpperCase()
    displayValue = t(`TRIPLOG.MODES.${modeKey}`)
  }

  const vehicle = vehiclesById[String(form.vehicleId)]

    if (key === 'fuelUsed') {
      const vehicle = vehiclesById[String(form.vehicleId)]
      const liters = calculateFuelUsedLiters(
        form,
        vehicle ? [vehicle] : []
      )
      displayValue = liters != null ? liters.toFixed(2) : ''
    }

if (key === 'duration') {
  const minutes = calculateTripDurationMinutes(
    form.startTimeIso,
    form.endTimeIso
  )

  const formatted = formatDurationHHMM(minutes)
  displayValue = formatted || ''
}

if (key === 'avgSpeedKmh') {
  const speed = calculateAvgSpeedKmh(form)
  displayValue = speed != null ? speed.toFixed(1) : ''
}

if (key === 'VIN') {
  displayValue =
    vehicle?.vin ||
    form.VIN ||
    ''
}

if (key === 'model') {
  displayValue = vehicle?.model || ''
}

if (key === 'typ_variant_verzia') {
  displayValue = vehicle?.variantType || ''
}

    if ((key === 'gpsStart' || key === 'gpsEnd') && form[key]) {
      if (typeof form[key] === 'object') {
        displayValue = `${form[key].latitude}, ${form[key].longitude}`
      }
    }

if (key === 'vehicleId') {
  displayValue = getTripVehicleLabel(form)
}

if (key === 'spz') {
  displayValue =
    vehicle?.licensePlate ||
    vehicle?.spz ||
    form.spz ||
    ''
}

    if (key === 'date' && form[key]) {
      displayValue = new Date(form[key]).toLocaleDateString('sk-SK')
    }

if ((key === 'startTimeIso' || key === 'endTimeIso') && form[key]) {
  displayValue = new Date(form[key]).toLocaleTimeString('sk-SK', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

    return (
      <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
        {key === 'note' ? (
          <textarea
            id={key}
            value={form[key] || ''}
disabled={
  !editMode ||
  (isGpsMode &&
    !['vehicleId', 'driver', 'purpose', 'note'].includes(key)) ||
  (isManualMode &&
    manualReadonlyFields.includes(key))
}
            onChange={e =>
              setForm(prev => ({ ...prev, [key]: e.target.value }))
            }
            rows={2}
            style={{
              ...inputStyle,
              resize: 'vertical',
    fontFamily: 'Arial, sans-serif',
background:
  !editMode ||
  (isGpsMode &&
    !['vehicleId', 'driver', 'purpose', 'note'].includes(key)) ||
  (isManualMode &&
    manualReadonlyFields.includes(key))
    ? '#ddd'
    : '#fff'
            }}
          />
) : (
  (key === 'vehicleId' && editMode) ? (
<select
  id={key}
  value={form.vehicleId ?? ''}
  onChange={e => {
    const vehicleId = e.target.value
    const vehicle = vehiclesById[String(vehicleId)]

    setForm(prev => ({
      ...prev,
      vehicleId,
      receiptId: '',
      spz: vehicle?.licensePlate || vehicle?.spz || '',
      VIN: vehicle?.vin || '',
      model: vehicle?.model || '',
      typ_variant_verzia: vehicle?.variantType || ''
    }))
  }}
      style={{ ...inputStyle, height: 34 }}
    >
      {Object.values(vehiclesById).map(v => (
<option key={v.id} value={v.id}>
  {`${v.name || v.model || '-'} (${v.licensePlate || v.spz || '-'})`}
</option>
      ))}
    </select>
) : (key === 'purpose' && editMode) ? (
  <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
    <select
      id={key}
      value={
        PURPOSE_KEYS.find(k => getPurposeLabel(k) === form.purpose) || 'OTHER'
      }
      onChange={e => {
        const val = e.target.value

        if (val === 'OTHER') {
          setForm(prev => ({
            ...prev,
            purpose: ''
          }))
          return
        }

        setForm(prev => ({
          ...prev,
          purpose: getPurposeLabel(val)
        }))
      }}
      style={{ ...inputStyle, height: 34 }}
    >
      {PURPOSE_KEYS.map(k => (
        <option key={k} value={k}>
          {getPurposeLabel(k)}
        </option>
      ))}
    </select>

    {( !PURPOSE_KEYS.some(k => getPurposeLabel(k) === form.purpose) ) && (
      <input
        type="text"
        value={form.purpose || ''}
        onChange={e =>
          setForm(prev => ({
            ...prev,
            purpose: e.target.value
          }))
        }
        placeholder={t('TRIPLOG.TRIPS.TABLE.purpose')}
        style={{
          ...inputStyle,
          marginTop: 6,
    maxWidth: '95%'
        }}
      />
    )}
  </div>
  ) : (
<input
  id={key}
  type={
  editMode
    ? key === 'date'
      ? 'date'
      : (key === 'startTimeIso' || key === 'endTimeIso')
        ? 'time'
        : 'text'
    : 'text'
}
value={
  key === 'duration' || key === 'fuelUsed' || key === 'avgSpeedKmh' || key === 'mode'
    ? displayValue
    : editMode
      ? (
          key === 'date'
            ? formatForDateInput(form[key])
            : (key === 'startTimeIso' || key === 'endTimeIso')
              ? (form[key]
                  ? (() => {
                      const d = new Date(form[key])
                      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
                    })()
                  : ''
                )
		: (
		    key === 'gpsStart' ||
 		    key === 'gpsEnd' ||
   		    key === 'spz' ||
   		    key === 'VIN'
   		   ? displayValue
   		   : (form[key] ?? '')
  		)
        )
      : displayValue
}
disabled={
  key === 'duration' ||
  key === 'distanceKm' ||
  !editMode ||
  (isGpsMode &&
    !['vehicleId', 'driver', 'purpose', 'note'].includes(key)) ||
  (isManualMode &&
    manualReadonlyFields.includes(key))
}
onChange={e => {
  setFieldErrors(prev => ({
    ...prev,
    [key]: false
  }))

  let val = e.target.value

  if (key === 'odoStart' || key === 'odoEnd') {
    val = val.replace(',', '.')
    if (!/^\d*\.?\d{0,2}$/.test(val)) {
      return
    }
  }

  if (key === 'startTimeIso' || key === 'endTimeIso') {
    const date = form.date
      ? new Date(form.date)
      : new Date()

    if (!val) {
      setForm(prev => ({ ...prev, [key]: '' }))
      return
    }

    const [h, m] = val.split(':')
    date.setHours(Number(h))
    date.setMinutes(Number(m))
    date.setSeconds(0)

    setForm(prev => ({
      ...prev,
      [key]: new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        0
      ).toISOString()
    }))
    return
  }

  setForm(prev => {
    const updated = { ...prev, [key]: val }

    if (key === 'odoStart' || key === 'odoEnd') {
      const odoStart = Number(updated.odoStart)
      const odoEnd = Number(updated.odoEnd)

      if (
        Number.isFinite(odoStart) &&
        Number.isFinite(odoEnd) &&
        odoEnd >= odoStart
      ) {
        updated.distanceKm = (odoEnd - odoStart).toFixed(2)
      }
    }

    return updated
  })
}}
onKeyDown={e => {
  if (!editMode) return

  if (key === 'date') {
    if (e.key === 'Tab') {
      e.preventDefault()

      const modalEl = modalRef.current
      if (!modalEl) return

      const focusable = Array.from(
        modalEl.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.disabled)

      const index = focusable.indexOf(e.target)

      if (e.shiftKey) {
        focusable[index - 1]?.focus()
      } else {
        focusable[index + 1]?.focus()
      }

      return
    }

    // pôvodné správanie – blokovanie písania
    e.preventDefault()
  }
}}
onClick={e => {
  if (
    editMode &&
    key === 'date' &&
    e.target.showPicker
  ) {
    e.target.showPicker()
  }
}}
style={{
  ...inputStyle,
  background:
    fieldErrors[key]
      ? '#ffdddd'
      : (
          key === 'duration' ||
          key === 'distanceKm' ||
          !editMode ||
          (isGpsMode &&
            !['vehicleId', 'driver', 'purpose', 'note'].includes(key)) ||
          (isManualMode &&
            manualReadonlyFields.includes(key))
              ? '#ddd'
              : '#fff'
        ),
  cursor:
    editMode &&
    (key === 'date' || key === 'startTimeIso' || key === 'endTimeIso') &&
    !isGpsMode
      ? 'pointer'
      : 'default'
}}
/>
    )
)}
      </Row>
    )
  }

  return (
    <>
      <Overlay open={loading && false} zIndex={4000} backdropOpacity={0.75} />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3100,
          padding: 20
        }}
      >
        <div
          ref={modalRef}
          tabIndex={-1}
          onClick={e => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: '5px 30px 10px 30px',
            border: '2px solid #107c6b',
            borderRadius: 14,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: '100%',
            maxWidth: modalMaxWidth,
            maxHeight: modalMaxHeight,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 5, marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: modalTitleFontSize }}>
              {t('TRIPLOG.DETAIL_TITLE')}
            </h3>

            <button
              type="button"
              style={modalPrimaryButtonStyle(
                pressedBtn,
                'showTripRoute',
                hoverBtn === 'showTripRoute'
              )}
              onMouseEnter={() => setHoverBtn('showTripRoute')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('showTripRoute')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={() => setRouteOpen(true)}
            >
              {t('TRIPLOG.FORM.TRIP_MAP')}
            </button>
          </div>

          {message && (
            <MessageBox message={message.message} type={message.type} />
          )}

	<div style={{ display: 'flex', flexDirection: 'column', gap: layout.rowGap, overflowY: 'auto' }}>
            {fields.map(renderField)}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 15, justifyContent: 'center' }}>
            {!editMode ? (
              <>
  {showTripNavigation && (
<button
  type="button"
  style={{
    ...modalSecondaryButtonStyle(
      pressedBtn,
      'prevTrip',
      hoverBtn === 'prevTrip'
    ),
    width: 50,
    minWidth: 40,
    padding: 0,
    opacity: !hasPrevTrip ? 0.4 : 1,
    cursor: !hasPrevTrip ? 'not-allowed' : 'pointer'
  }}
  disabled={!hasPrevTrip}
  onMouseEnter={() => setHoverBtn('prevTrip')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('prevTrip')}
  onMouseUp={() => setPressedBtn(null)}
  onClick={() => onPrevTrip?.()}
>
  <img
    src={arrowLeft}
    alt="prev"
    style={{
      width: 18,
      height: 18,
      pointerEvents: 'none',
      filter: 'invert(33%) sepia(24%) saturate(746%) hue-rotate(120deg) brightness(85%) contrast(90%)'
    }}
  />
</button>
  )}
	<button
 	 style={modalPrimaryButtonStyle(
 	   pressedBtn,
 	   'editTrip',
  	  hoverBtn === 'editTrip'
	  )}
 	 onMouseEnter={() => setHoverBtn('editTrip')}
 	 onMouseLeave={() => {
  	  setHoverBtn(null)
  	  setPressedBtn(null)
	  }}
	  onMouseDown={() => setPressedBtn('editTrip')}
	  onMouseUp={() => setPressedBtn(null)}
	  onClick={() => setEditMode(true)}
	>
 	 {t('EDIT_PROFILE')}
	</button>

	<button
  	style={modalSecondaryButtonStyle(
   	 pressedBtn,
   	 'closeTrip',
  	  hoverBtn === 'closeTrip'
 	 )}
 	 onMouseEnter={() => setHoverBtn('closeTrip')}
 	 onMouseLeave={() => {
 	   setHoverBtn(null)
  	  setPressedBtn(null)
 	 }}
  	onMouseDown={() => setPressedBtn('closeTrip')}
 	 onMouseUp={() => setPressedBtn(null)}
 	 onClick={onClose}
	>
 	 {t('CLOSE')}
	</button>
  {showTripNavigation && (
<button
  type="button"
  style={{
    ...modalSecondaryButtonStyle(
      pressedBtn,
      'nextTrip',
      hoverBtn === 'nextTrip'
    ),
    width: 50,
    minWidth: 40,
    padding: 0,
    opacity: !hasNextTrip ? 0.4 : 1,
    cursor: !hasNextTrip ? 'not-allowed' : 'pointer'
  }}
  disabled={!hasNextTrip}
  onMouseEnter={() => setHoverBtn('nextTrip')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('nextTrip')}
  onMouseUp={() => setPressedBtn(null)}
  onClick={() => onNextTrip?.()}
>
  <img
    src={arrowRight}
    alt="next"
    style={{
      width: 18,
      height: 18,
      pointerEvents: 'none',
      filter: 'invert(33%) sepia(24%) saturate(746%) hue-rotate(120deg) brightness(85%) contrast(90%)'
    }}
  />
</button>
  )}
              </>
            ) : (
              <>
	<button
	  style={modalPrimaryButtonStyle(
	    pressedBtn,
 	   'saveTrip',
 	   hoverBtn === 'saveTrip'
	  )}
	  onMouseEnter={() => setHoverBtn('saveTrip')}
 	 onMouseLeave={() => {
 	   setHoverBtn(null)
 	   setPressedBtn(null)
 	 }}
 	 onMouseDown={() => setPressedBtn('saveTrip')}
 	 onMouseUp={() => setPressedBtn(null)}
 	 onClick={saveTrip}
	>
 	 {t('SAVE')}
	</button>

	<button
	  style={modalSecondaryButtonStyle(
 	   pressedBtn,
 	   'cancelTrip',
 	   hoverBtn === 'cancelTrip'
 	 )}
	  onMouseEnter={() => setHoverBtn('cancelTrip')}
 	 onMouseLeave={() => {
 	   setHoverBtn(null)
 	   setPressedBtn(null)
 	 }}
	  onMouseDown={() => setPressedBtn('cancelTrip')}
 	 onMouseUp={() => setPressedBtn(null)}
onClick={() => {
  setForm(original)
  setEditMode(false)
  setFieldErrors({})
  setMessage(null)
}}
	>
  	{t('CANCEL')}
	</button>
              </>
            )}
          </div>
        </div>
      </div>
{showFullscreenHint && (
  <div
    style={{
      position: 'fixed',
      top: cursorPos.y - 50,
      left: cursorPos.x,
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#d9ffe4',
      padding: '6px 14px 8px 14px',
      borderRadius: 12,
      fontSize: 16,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 5000,
      border: '1.5px solid #107c6b',
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1
    }}
  >
    {t('TRIPLOG.BUTTON.MAP_PICK')}
  </div>
)}
<TripRouteModal
  open={routeOpen}
  onClose={() => setRouteOpen(false)}
  trip={form}
/>
<MapPickerModal
  open={mapOpen}
  onClose={() => setMapOpen(false)}
  onSelect={handleMapSelect}
/>
<ReceiptAddModal
  open={receiptModalOpen}
  userId={trip?.userId}
  onClose={() => setReceiptModalOpen(false)}
  onSaved={(created) => {
    setReceiptModalOpen(false)
    loadReceipts()
    if (created?.id) {
      setForm(prev => ({ ...prev, receiptId: String(created.id) }))
    }
  }}
/>
    </>
  )
}

export default TripDetailModal